import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setUserInfo } from '../store/userSlice';
import { RegistrationService, AuthenticationService, DictionaryControllerService, SportsClubControllerService, TeamControllerService, GeocodingControllerService } from '../api/index.ts';
import MapPickerModal from './MapPickerModal';
import './Register.css';

const MAX_REGISTER_ATTEMPTS = 3;
const REGISTER_LOCKOUT_MS = 60_000;

function Register() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Whitelist bezpiecznych ścieżek dla ?returnTo=
  const isSafeReturnTo = (path) => typeof path === 'string' && path.startsWith('/preseason-game-invitations/');
  const returnToParam = new URLSearchParams(location.search).get('returnTo');
  const safeReturnTo = isSafeReturnTo(returnToParam) ? returnToParam : null;

  const [currentStep, setCurrentStep] = useState(1);
  const [userTypes, setUserTypes] = useState([]);
  const [selectedUserType, setSelectedUserType] = useState('SPORTS_CLUB');
  const [isLoadingUserTypes, setIsLoadingUserTypes] = useState(false);
  const [territories, setTerritories] = useState([]);
  const [dictionaries, setDictionaries] = useState({});

  // Invitation token state
  const [invitationToken, setInvitationToken] = useState(null);
  const [invitationLoading, setInvitationLoading] = useState(false);
  const [invitationError, setInvitationError] = useState('');
  const [invitationClubName, setInvitationClubName] = useState('');
  const [invitationSportsClubId, setInvitationSportsClubId] = useState(null);
  const [invitationUserExists, setInvitationUserExists] = useState(false);
  const [invitationTeams, setInvitationTeams] = useState([]);

  // Wspólne pola
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Pola dla trenera
  const [coachData, setCoachData] = useState({
    firstName: '',
    lastName: '',
    selectedClubId: null,
    selectedTeamIds: []
  });

  // Drużyny dla trenera
  const [coachTeams, setCoachTeams] = useState([]);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [coachSelectedTeamsInfo, setCoachSelectedTeamsInfo] = useState([]); // { teamId, teamName, sportsClubName }

  // Modal wizarda dodawania drużyny (coach)
  const [showCoachAddTeamModal, setShowCoachAddTeamModal] = useState(false);
  const [coachWizardStep, setCoachWizardStep] = useState(1);
  const [wizardTempSelectedTeamIds, setWizardTempSelectedTeamIds] = useState([]);

  // Wyszukiwarka dla trenera
  const [coachSearchCriteria, setCoachSearchCriteria] = useState({
    voivodeship: '',
    county: ''
  });
  const [coachSearchResults, setCoachSearchResults] = useState([]);
  const [isLoadingCoachSearch, setIsLoadingCoachSearch] = useState(false);
  const [coachSearchError, setCoachSearchError] = useState('');
  const [coachSearchPagination, setCoachSearchPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    pageSize: 10,
    isFirst: true,
    isLast: true
  });

  // Pola dla klubu
  const [clubData, setClubData] = useState({
    regon: '',
    nip: '',
    name: '',
    city: '',
    latitude: null,
    longitude: null,
    voivodeship: '',
    county: '',
    teams: []
  });

  // City map picker state
  const [cityMapOpen, setCityMapOpen] = useState(false);

  // Modal dla dodawania drużyn
  const [showSeniorTeamModal, setShowSeniorTeamModal] = useState(false);
  const [showYouthTeamModal, setShowYouthTeamModal] = useState(false);
  const [editingTeamIndex, setEditingTeamIndex] = useState(null);
  const [newSeniorTeam, setNewSeniorTeam] = useState({
    teamType: 'SENIOR',
    seniorTier: '',
    seniorTeamType: '',
    gender: '',
    teamMode: 'SEASONAL',
    isLookingForPlayer: false,
    isLookingForCoach: false
  });
  const [newYouthTeam, setNewYouthTeam] = useState({
    teamType: 'YOUTH',
    youthTeamType: '',
    yearOfBirth: '',
    youthTier: '',
    gender: '',
    teamMode: 'SEASONAL',
    isLookingForPlayer: false,
    isLookingForCoach: false
  });

  // Logo klubu
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoError, setLogoError] = useState('');

  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [regonError, setRegonError] = useState('');
  const [nipError, setNipError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Śledzenie pól, które straciły focus (touched)
  const [touched, setTouched] = useState({});

  // Honeypot anty-bot
  const honeypotRef = useRef('');

  // Rate limiting rejestracji
  const registerAttemptsRef = useRef(0);
  const [registerLockoutUntil, setRegisterLockoutUntil] = useState(null);

  const from = safeReturnTo || location.state?.from || '/';

  // Funkcja walidacji REGON
  const validateRegon = (regon) => {
    // Usuń myślniki i spacje
    const cleanRegon = regon.replace(/[-\s]/g, '');

    // Sprawdź czy ma 9 cyfr
    if (!/^\d{9}$/.test(cleanRegon)) {
      return { isValid: false, error: 'REGON musi składać się z 9 cyfr' };
    }

    // Wagi dla kolejnych cyfr REGON
    const weights = [8, 9, 2, 3, 4, 5, 6, 7];

    // Oblicz sumę kontrolną
    let sum = 0;
    for (let i = 0; i < 8; i++) {
      sum += parseInt(cleanRegon[i]) * weights[i];
    }

    // Sprawdź cyfrę kontrolną
    const checkDigit = sum % 11;
    const expectedDigit = checkDigit === 10 ? 0 : checkDigit;
    if (expectedDigit !== parseInt(cleanRegon[8])) {
      return { isValid: false, error: 'Nieprawidłowy numer REGON' };
    }

    return { isValid: true, error: '' };
  };

  // Handler zmiany REGON (bez walidacji - walidacja na blur)
  const handleRegonChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setClubData(prev => ({ ...prev, regon: value }));
    if (touched.regon) {
      if (value.trim()) {
        const validation = validateRegon(value);
        setRegonError(validation.error);
      } else {
        setRegonError('');
      }
    }
  };

  const handleRegonBlur = () => {
    setTouched(prev => ({ ...prev, regon: true }));
    const value = clubData.regon;
    if (value.trim()) {
      const validation = validateRegon(value);
      setRegonError(validation.error);
    } else {
      setRegonError('');
    }
  };

  // Funkcja walidacji NIP
  const validateNip = (nip) => {
    const cleanNip = nip.replace(/[-\s]/g, '');

    if (!/^\d{10}$/.test(cleanNip)) {
      return { isValid: false, error: 'NIP musi składać się z 10 cyfr' };
    }

    const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cleanNip[i]) * weights[i];
    }

    const checkDigit = sum % 11;
    if (checkDigit === 10 || checkDigit !== parseInt(cleanNip[9])) {
      return { isValid: false, error: 'Nieprawidłowy numer NIP' };
    }

    return { isValid: true, error: '' };
  };

  // Handler zmiany NIP (bez walidacji - walidacja na blur)
  const handleNipChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setClubData(prev => ({ ...prev, nip: value }));
    if (touched.nip) {
      if (value.trim()) {
        const validation = validateNip(value);
        setNipError(validation.error);
      } else {
        setNipError('');
      }
    }
  };

  const handleNipBlur = () => {
    setTouched(prev => ({ ...prev, nip: true }));
    const value = clubData.nip;
    if (value.trim()) {
      const validation = validateNip(value);
      setNipError(validation.error);
    } else {
      setNipError('');
    }
  };

  // Handler zmiany email (bez walidacji - walidacja na blur)
  const handleEmailChange = (value) => {
    setEmail(value);
    if (touched.email) {
      if (value.trim()) {
        const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
        setEmailError(isValid ? '' : 'Podaj poprawny adres email');
      } else {
        setEmailError('');
      }
    }
  };

  const handleEmailBlur = () => {
    setTouched(prev => ({ ...prev, email: true }));
    if (email.trim()) {
      const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
      setEmailError(isValid ? '' : 'Podaj poprawny adres email');
    } else {
      setEmailError('');
    }
  };

  // Walidacja hasła
  const validatePassword = (pwd) => {
    const errors = [];
    if (pwd.length < 8) errors.push('Min. 8 znaków');
    if (!/[A-Z]/.test(pwd)) errors.push('Min. 1 wielka litera');
    if (!/[a-z]/.test(pwd)) errors.push('Min. 1 mała litera');
    if (!/[0-9]/.test(pwd)) errors.push('Min. 1 cyfra');
    return errors;
  };

  const handlePasswordChange = (value) => {
    setPassword(value);
    if (touched.password) {
      if (value) {
        const errors = validatePassword(value);
        setPasswordError(errors.length > 0 ? errors.join(', ') : '');
      } else {
        setPasswordError('');
      }
    }
  };

  const handlePasswordBlur = () => {
    setTouched(prev => ({ ...prev, password: true }));
    if (password) {
      const errors = validatePassword(password);
      setPasswordError(errors.length > 0 ? errors.join(', ') : '');
    } else {
      setPasswordError('');
    }
  };

  const handleConfirmPasswordBlur = () => {
    setTouched(prev => ({ ...prev, confirmPassword: true }));
  };

  // Handler zmiany logo z walidacją rozdzielczości, rozmiaru i magic bytes
  const MAX_LOGO_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB
  const ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
  const PNG_MAGIC = [0x89, 0x50, 0x4E, 0x47];
  const JPEG_MAGIC = [0xFF, 0xD8, 0xFF];
  const WEBP_MAGIC_RIFF = [0x52, 0x49, 0x46, 0x46];

  const validateFileMagicBytes = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const arr = new Uint8Array(e.target.result);
        const isPng = PNG_MAGIC.every((b, i) => arr[i] === b);
        const isJpeg = JPEG_MAGIC.every((b, i) => arr[i] === b);
        const isWebp = WEBP_MAGIC_RIFF.every((b, i) => arr[i] === b);
        resolve(isPng || isJpeg || isWebp);
      };
      reader.onerror = () => resolve(false);
      reader.readAsArrayBuffer(file.slice(0, 12));
    });
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    setLogoError('');

    if (!file) {
      setLogoFile(null);
      setLogoPreview(null);
      return;
    }

    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      setLogoError('Logo musi być w formacie PNG, JPEG lub WebP');
      e.target.value = '';
      return;
    }

    if (file.size > MAX_LOGO_SIZE_BYTES) {
      setLogoError(`Maksymalny rozmiar pliku to 2 MB. Wybrany plik: ${(file.size / 1024 / 1024).toFixed(1)} MB`);
      e.target.value = '';
      return;
    }

    const isValidContent = await validateFileMagicBytes(file);
    if (!isValidContent) {
      setLogoError('Plik nie jest prawidłowym obrazem. Wybierz plik PNG, JPEG lub WebP.');
      e.target.value = '';
      return;
    }

    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      if (img.width > 1024 || img.height > 1024) {
        setLogoError(`Maksymalna rozdzielczość to 1024x1024 px. Wybrany obraz: ${img.width}x${img.height} px`);
        setLogoFile(null);
        setLogoPreview(null);
        URL.revokeObjectURL(objectUrl);
        e.target.value = '';
      } else {
        setLogoFile(file);
        setLogoPreview(objectUrl);
      }
    };
    img.onerror = () => {
      setLogoError('Nie udało się odczytać pliku graficznego');
      URL.revokeObjectURL(objectUrl);
      e.target.value = '';
    };
    img.src = objectUrl;
  };

  const handleRemoveLogo = () => {
    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoFile(null);
    setLogoPreview(null);
    setLogoError('');
  };

  // City map picker handler
  const handleCityMapConfirm = ({ latitude, longitude, displayName, searchTerm }) => {
    const city = searchTerm || displayName;
    setClubData(prev => ({
      ...prev,
      city,
      latitude,
      longitude
    }));
    setCityMapOpen(false);
  };

  // Sprawdź token zaproszenia przy montowaniu
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('invitation');

    if (token) {
      setInvitationToken(token);
      setInvitationLoading(true);
      RegistrationService.validateInvitationToken(token)
        .then((result) => {
          if (result.valid) {
            setSelectedUserType('COACH');
            setInvitationClubName(result.sportsClubName || '');
            setInvitationSportsClubId(result.sportsClubId || null);
            setInvitationUserExists(result.userAlreadyExists || false);
            setInvitationTeams(result.teams || []);
            if (result.email && !result.userAlreadyExists) {
              setEmail(result.email);
            }
            setCurrentStep(1);
          } else {
            setInvitationError('Nieprawidłowy lub wygasły link zaproszenia');
          }
        })
        .catch(() => {
          setInvitationError('Nieprawidłowy lub wygasły link zaproszenia');
        })
        .finally(() => {
          setInvitationLoading(false);
        });
    } else {
      setSelectedUserType('SPORTS_CLUB');
      setCurrentStep(1);
    }
  }, [location.search]);

  // Pobierz słowniki (województwa i powiaty) przy montowaniu
  useEffect(() => {
    const fetchDictionaries = async () => {
      try {
        const dictionariesData = await DictionaryControllerService.getDictionaries();

        if (dictionariesData?.territories) {
          setTerritories(dictionariesData.territories);
        }
        setDictionaries(dictionariesData);
      } catch (error) {
        // error handled silently
      }
    };

    fetchDictionaries();
  }, []);

  // Pobierz drużyny dla wybranego klubu (trener)
  useEffect(() => {
    const fetchTeamsForClub = async () => {
      if (selectedUserType === 'COACH' && coachData.selectedClubId) {
        setIsLoadingTeams(true);
        try {
          const teams = await TeamControllerService.getTeamsBySportsClubId(coachData.selectedClubId);
          setCoachTeams(teams || []);
        } catch (error) {
          setCoachTeams([]);
        } finally {
          setIsLoadingTeams(false);
        }
      } else {
        setCoachTeams([]);
      }
    };

    fetchTeamsForClub();
  }, [selectedUserType, coachData.selectedClubId]);


  const handleNext = () => {
    setError('');
    setCurrentStep(prev => prev + 1);
  };

  // Helper do pobierania powiatów dla wybranego województwa
  const getCountiesForVoivodeship = (voivodeshipKey, userType) => {
    const territory = territories.find(t => t.key === voivodeshipKey);
    return territory?.subValues || [];
  };

  // Funkcje formatujące dla wyświetlania drużyn
  const formatSeniorTier = (tier) => {
    const labels = {
      'EKSTRAKLASA': 'Ekstraklasa',
      'FIRST_LEAGUE': 'I Liga',
      'SECOND_LEAGUE': 'II Liga',
      'THIRD_LEAGUE': 'III Liga',
      'FOURTH_LEAGUE': 'IV Liga',
      'FIFTH_LEAGUE': 'V Liga',
      'REGIONAL_CLASS': 'Klasa Okręgowa',
      'CLASS_A': 'Klasa A',
      'CLASS_B': 'Klasa B',
      'CLASS_C': 'Klasa C'
    };
    return labels[tier] || tier;
  };

  const formatSeniorTeamType = (type) => {
    const labels = {
      'FIRST_TEAM': 'Pierwsza drużyna',
      'SECOND_TEAM': 'Druga drużyna',
      'THIRD_TEAM': 'Trzecia drużyna'
    };
    return labels[type] || type;
  };

  const formatYouthTier = (tier) => {
    const labels = {
      'CENTRAL_YOUTH_LEAGUE': 'Centralna Liga Juniorów',
      'YOUTH_FIRST_LEAGUE': 'Juniorska I Liga',
      'YOUTH_SECOND_LEAGUE': 'Juniorska II Liga',
      'REGIONAL_YOUTH_LEAGUE': 'Okręgowa Liga Juniorów',
      'YOUTH_CLASS_A': 'Juniorska Klasa A',
      'YOUTH_CLASS_B': 'Juniorska Klasa B'
    };
    return labels[tier] || tier;
  };

  const formatAgeCategory = (category) => {
    const labels = {
      'YEAR_2007': 'Rocznik 2007',
      'YEAR_2008': 'Rocznik 2008',
      'YEAR_2009': 'Rocznik 2009',
      'YEAR_2010': 'Rocznik 2010',
      'YEAR_2011': 'Rocznik 2011',
      'YEAR_2012': 'Rocznik 2012',
      'YEAR_2013': 'Rocznik 2013',
      'YEAR_2014': 'Rocznik 2014',
      'YEAR_2015': 'Rocznik 2015',
      'YEAR_2016': 'Rocznik 2016',
      'YEAR_2017': 'Rocznik 2017',
      'YEAR_2018': 'Rocznik 2018',
      'YEAR_2019': 'Rocznik 2019',
      'YEAR_2020': 'Rocznik 2020'
    };
    return labels[category] || category;
  };

  // Funkcja sprawdzająca czy wszystkie wymagane pola na danym kroku są wypełnione
  const isCurrentStepValid = () => {
    // TRENER (z zaproszenia)
    if (selectedUserType === 'COACH') {
      if (invitationUserExists) {
        // Existing user: krok 1 = wybór drużyn
        if (currentStep === 1) {
          return coachData.selectedTeamIds.length > 0;
        }
      } else {
        // New user: krok 1=dane osobowe, 2=drużyny, 3=hasło
        if (currentStep === 1) {
          return coachData.firstName && coachData.lastName;
        }
        if (currentStep === 2) {
          return coachData.selectedTeamIds.length > 0;
        }
        if (currentStep === 3) {
          return email && !emailError && password && !passwordError && confirmPassword && password === confirmPassword && termsAccepted;
        }
      }
    }

    // KLUB: krok 1=dane klubu, 2=drużyny, 3=hasło
    if (selectedUserType === 'SPORTS_CLUB') {
      if (currentStep === 1) {
        const regonValidation = validateRegon(clubData.regon);
        const nipValidation = validateNip(clubData.nip);
        return clubData.regon && regonValidation.isValid && clubData.nip && nipValidation.isValid && clubData.name && clubData.voivodeship && clubData.county;
      }
      if (currentStep === 2) {
        return clubData.teams.length > 0;
      }
      if (currentStep === 3) {
        return email && !emailError && password && !passwordError && confirmPassword && password === confirmPassword && termsAccepted;
      }
    }

    return false;
  };

  const handleBack = () => {
    setError('');
    setCurrentStep(prev => Math.max(1, prev - 1));
  };

  // Funkcje dla wyszukiwarki trenera
  const handleCoachSearchChange = (e) => {
    const { name, value } = e.target;
    if (name === 'voivodeship') {
      setCoachSearchCriteria(prev => ({
        ...prev,
        voivodeship: value,
        county: ''
      }));
    } else {
      setCoachSearchCriteria(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const getCountiesForCoachSearch = () => {
    if (!coachSearchCriteria.voivodeship) {
      return [];
    }
    const territory = territories.find(t => t.key === coachSearchCriteria.voivodeship);
    return territory?.subValues || [];
  };

  const performCoachSearch = async (page = 0) => {
    setIsLoadingCoachSearch(true);
    setCoachSearchError('');

    try {
      const criteria = {
        county: coachSearchCriteria.county || undefined,
        voivodeship: coachSearchCriteria.voivodeship || undefined
      };

      const response = await SportsClubControllerService.searchSportsClubs(
        criteria,
        page,
        coachSearchPagination.pageSize
      );

      setCoachSearchResults(response.content || []);
      setCoachSearchPagination({
        currentPage: response.number ?? 0,
        totalPages: response.totalPages ?? 0,
        totalElements: response.totalElements ?? 0,
        pageSize: response.size ?? 10,
        isFirst: response.first ?? true,
        isLast: response.last ?? true
      });

      if (!response.content || response.content.length === 0) {
        setCoachSearchError('Nie znaleziono klubów spełniających kryteria');
      }
    } catch (error) {
      setCoachSearchError('Wystąpił błąd podczas wyszukiwania');
    } finally {
      setIsLoadingCoachSearch(false);
    }
  };

  const handleCoachSearch = async (e) => {
    e.preventDefault();
    await performCoachSearch();
  };

  const handleSelectClub = (club) => {
    setCoachData(prev => ({ ...prev, selectedClubId: club.id }));
    setCoachSearchError('');
  };

  const handleResetCoachSearch = () => {
    setCoachSearchCriteria({
      voivodeship: '',
      county: ''
    });
    setCoachSearchResults([]);
    setCoachSearchError('');
    setCoachSearchPagination({
      currentPage: 0,
      totalPages: 0,
      totalElements: 0,
      pageSize: 10,
      isFirst: true,
      isLast: true
    });
  };

  const openCoachAddTeamModal = () => {
    setShowCoachAddTeamModal(true);
    setCoachWizardStep(1);
    setWizardTempSelectedTeamIds([]);
    setCoachData(prev => ({ ...prev, selectedClubId: null }));
    setCoachTeams([]);
    handleResetCoachSearch();
  };

  const closeCoachAddTeamModal = () => {
    setShowCoachAddTeamModal(false);
    setCoachWizardStep(1);
    setWizardTempSelectedTeamIds([]);
  };

  const handleWizardConfirmTeams = () => {
    // Add selected teams to the main list (avoid duplicates)
    const newTeamsInfo = wizardTempSelectedTeamIds
      .filter(id => !coachData.selectedTeamIds.includes(id))
      .map(id => {
        const team = coachTeams.find(t => t.teamId === id);
        const club = coachSearchResults.find(c => c.id === coachData.selectedClubId);
        return {
          teamId: id,
          teamName: team?.name || `Drużyna #${id}`,
          sportsClubName: club?.name || '',
          tier: team?.tier || ''
        };
      });

    setCoachData(prev => ({
      ...prev,
      selectedTeamIds: [...prev.selectedTeamIds, ...newTeamsInfo.map(t => t.teamId)]
    }));
    setCoachSelectedTeamsInfo(prev => [...prev, ...newTeamsInfo]);
    closeCoachAddTeamModal();
  };

  const handleRemoveCoachTeam = (teamId) => {
    setCoachData(prev => ({
      ...prev,
      selectedTeamIds: prev.selectedTeamIds.filter(id => id !== teamId)
    }));
    setCoachSelectedTeamsInfo(prev => prev.filter(t => t.teamId !== teamId));
  };

  const handleCoachSearchNextPage = () => {
    if (!coachSearchPagination.isLast) {
      performCoachSearch(coachSearchPagination.currentPage + 1);
    }
  };

  const handleCoachSearchPreviousPage = () => {
    if (!coachSearchPagination.isFirst) {
      performCoachSearch(coachSearchPagination.currentPage - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setValidationErrors([]);

    // Honeypot check — bot wypełnił ukryte pole, cicho blokujemy
    if (honeypotRef.current) {
      setSuccessMessage(selectedUserType === 'COACH' ? 'COACH_SUCCESS' : 'CLUB_SUCCESS');
      return;
    }

    // Rate limiting
    if (registerLockoutUntil && Date.now() < registerLockoutUntil) {
      const secondsLeft = Math.ceil((registerLockoutUntil - Date.now()) / 1000);
      setError(`Zbyt wiele prób rejestracji. Spróbuj ponownie za ${secondsLeft} s.`);
      return;
    }
    if (registerLockoutUntil && Date.now() >= registerLockoutUntil) {
      setRegisterLockoutUntil(null);
      registerAttemptsRef.current = 0;
    }

    // Existing user accepting invitation — no email/password needed
    if (selectedUserType === 'COACH' && invitationUserExists) {
      if (coachData.selectedTeamIds.length === 0) {
        setError('Wybierz przynajmniej jedną drużynę');
        return;
      }
      setIsLoading(true);
      try {
        await RegistrationService.acceptInvitation({
          token: invitationToken,
          teamIds: coachData.selectedTeamIds
        });
        registerAttemptsRef.current = 0;
        setSuccessMessage('COACH_ACCEPT_SUCCESS');
      } catch (error) {
        registerAttemptsRef.current += 1;
        if (registerAttemptsRef.current >= MAX_REGISTER_ATTEMPTS) {
          setRegisterLockoutUntil(Date.now() + REGISTER_LOCKOUT_MS);
          setError(`Zbyt wiele prób. Spróbuj ponownie za ${REGISTER_LOCKOUT_MS / 1000} s.`);
        } else if (error.status === 400) {
          setError('Nieprawidłowe dane. Sprawdź formularz i spróbuj ponownie.');
        } else {
          setError('Wystąpił błąd. Spróbuj ponownie później.');
        }
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Walidacja wspólnych pól
    if (!email || !password || !confirmPassword) {
      setError('Wypełnij wszystkie wymagane pola');
      return;
    }

    if (passwordError) {
      setError('Hasło nie spełnia wymagań');
      return;
    }

    if (password !== confirmPassword) {
      setError('Hasła nie są zgodne');
      return;
    }

    setIsLoading(true);

    try {
      if (selectedUserType === 'COACH') {
        const coachPayload = {
          email: email.trim(),
          password,
          firstName: coachData.firstName.trim(),
          lastName: coachData.lastName.trim(),
          teamIds: coachData.selectedTeamIds,
          invitationToken: invitationToken || undefined,
          termsAccepted: true,
          companyUrl: honeypotRef.current || undefined
        };

        await RegistrationService.registerCoach({
          data: new Blob([JSON.stringify(coachPayload)], { type: 'application/json' })
        });
      } else if (selectedUserType === 'SPORTS_CLUB') {
        // Mapuj drużyny do nowego formatu CreateTeamDto
        const teams = clubData.teams.map(team => {
          if (team.teamType === 'SENIOR') {
            return {
              teamType: 'SENIOR',
              teamSubtype: team.seniorTeamType || undefined,
              tier: team.seniorTier || undefined,
              gender: team.gender || undefined,
              teamMode: team.teamMode || 'SEASONAL'
            };
          } else {
            // YOUTH
            return {
              teamType: 'YOUTH',
              teamSubtype: team.youthTeamType || undefined,
              tier: team.youthTier || undefined,
              gender: team.gender || undefined,
              yearOfBirth: team.yearOfBirth || undefined,
              teamMode: team.teamMode || 'SEASONAL'
            };
          }
        });

        const sportsClubData = {
            email: email.trim(),
            password,
            regon: clubData.regon.trim(),
            nip: clubData.nip.trim(),
            name: clubData.name.trim(),
            city: clubData.city ? clubData.city.trim() : undefined,
            latitude: clubData.latitude || undefined,
            longitude: clubData.longitude || undefined,
            voivodeship: clubData.voivodeship || undefined,
            county: clubData.county || undefined,
            teams: teams.length > 0 ? teams : undefined,
            termsAccepted: true,
            companyUrl: honeypotRef.current || undefined
        };

        await RegistrationService.registerSportsClub({
          data: new Blob([JSON.stringify(sportsClubData)], { type: 'application/json' }),
          image: logoFile || undefined
        });
      }

      registerAttemptsRef.current = 0;

      // Auto-login w ścieżce z linku zaproszenia (?returnTo=) — tylko dla SPORTS_CLUB
      if (safeReturnTo && selectedUserType !== 'COACH') {
        try {
          const loginResponse = await AuthenticationService.login({
            email: email.trim(),
            password
          });
          const userInfo = await AuthenticationService.getLoggedUserInfo();
          if (loginResponse?.userId && loginResponse?.userType) {
            dispatch(setUserInfo({
              userId: loginResponse.userId,
              username: loginResponse.username,
              name: userInfo.name || loginResponse.name,
              userType: loginResponse.userType,
              roles: userInfo.roles || loginResponse.roles || [],
              teams: userInfo.teams || [],
              sportsClubId: userInfo.sportsClubId,
              sportsClubName: userInfo.sportsClubName,
              logoUrl: userInfo.logoUrl,
              expressModeEnabled: userInfo.expressModeEnabled,
              expressModeThreshold: userInfo.expressModeThreshold,
              isApproved: userInfo.isApproved ?? loginResponse.isApproved
            }));
            navigate(safeReturnTo, { replace: true });
            return;
          }
        } catch (autoLoginErr) {
          // Jeśli auto-login się nie uda — fallback do standardowego success view
          console.error('Auto-login po rejestracji nie powiódł się', autoLoginErr);
        }
      }

      setSuccessMessage(selectedUserType === 'COACH' ? 'COACH_SUCCESS' : 'CLUB_SUCCESS');
    } catch (error) {
      registerAttemptsRef.current += 1;
      if (registerAttemptsRef.current >= MAX_REGISTER_ATTEMPTS) {
        setRegisterLockoutUntil(Date.now() + REGISTER_LOCKOUT_MS);
        setError(`Zbyt wiele prób rejestracji. Spróbuj ponownie za ${REGISTER_LOCKOUT_MS / 1000} s.`);
      } else if (error.status === 400) {
        setError('Dane rejestracyjne są nieprawidłowe. Sprawdź formularz i spróbuj ponownie.');
      } else if (error.status === 409) {
        setError('Konto z podanym adresem email już istnieje.');
      } else {
        setError('Wystąpił błąd podczas rejestracji. Spróbuj ponownie później.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const addSeniorTeam = () => {
    if (!newSeniorTeam.seniorTeamType) {
      setError('Podaj rodzaj drużyny');
      return;
    }
    if (!newSeniorTeam.seniorTier) {
      setError('Podaj poziom rozgrywkowy drużyny');
      return;
    }
    if (!newSeniorTeam.gender) {
      setError('Podaj płeć');
      return;
    }

    if (editingTeamIndex !== null) {
      // Edycja istniejącej drużyny
      setClubData(prev => ({
        ...prev,
        teams: prev.teams.map((team, idx) =>
          idx === editingTeamIndex ? { ...newSeniorTeam } : team
        )
      }));
      setEditingTeamIndex(null);
    } else {
      // Dodawanie nowej drużyny
      setClubData(prev => ({
        ...prev,
        teams: [...prev.teams, { ...newSeniorTeam }]
      }));
    }

    setNewSeniorTeam({ teamType: 'SENIOR', seniorTier: '', seniorTeamType: '', gender: '', teamMode: 'SEASONAL', isLookingForPlayer: false, isLookingForCoach: false });
    setShowSeniorTeamModal(false);
    setError('');
  };

  const addYouthTeam = () => {
    if (!newYouthTeam.youthTeamType) {
      setError('Podaj rodzaj drużyny');
      return;
    }
    if (!newYouthTeam.yearOfBirth) {
      setError('Podaj rocznik');
      return;
    }
    if (!newYouthTeam.youthTier) {
      setError('Podaj poziom rozgrywkowy drużyny');
      return;
    }
    if (!newYouthTeam.gender) {
      setError('Podaj płeć');
      return;
    }

    if (editingTeamIndex !== null) {
      // Edycja istniejącej drużyny
      setClubData(prev => ({
        ...prev,
        teams: prev.teams.map((team, idx) =>
          idx === editingTeamIndex ? { ...newYouthTeam } : team
        )
      }));
      setEditingTeamIndex(null);
    } else {
      // Dodawanie nowej drużyny
      setClubData(prev => ({
        ...prev,
        teams: [...prev.teams, { ...newYouthTeam }]
      }));
    }

    setNewYouthTeam({ teamType: 'YOUTH', youthTeamType: '', yearOfBirth: '', youthTier: '', gender: '', teamMode: 'SEASONAL', isLookingForPlayer: false, isLookingForCoach: false });
    setShowYouthTeamModal(false);
    setError('');
  };

  const editTeam = (index) => {
    const team = clubData.teams[index];
    setEditingTeamIndex(index);

    if (team.teamType === 'SENIOR') {
      setNewSeniorTeam({ ...team });
      setShowSeniorTeamModal(true);
    } else {
      setNewYouthTeam({ ...team });
      setShowYouthTeamModal(true);
    }
  };

  const removeTeam = (index) => {
    setClubData(prev => ({
      ...prev,
      teams: prev.teams.filter((_, i) => i !== index)
    }));
  };

  const getTotalSteps = () => {
    if (selectedUserType === 'COACH') {
      if (invitationUserExists) return 1; // tylko wybór drużyn
      return 3; // dane osobowe, drużyny, hasło
    }
    if (selectedUserType === 'SPORTS_CLUB') return 3; // dane klubu, drużyny, hasło
    return 3;
  };

  const renderStepIndicator = () => {
    const totalSteps = getTotalSteps();
    return (
      <div className="step-indicator">
        {Array.from({ length: totalSteps }, (_, i) => (
          <div
            key={i}
            className={`step ${currentStep === i + 1 ? 'active' : ''} ${currentStep > i + 1 ? 'completed' : ''}`}
          >
            {i + 1}
          </div>
        ))}
      </div>
    );
  };

  const renderInvitationTeamSelection = () => {
    const seniorTeams = invitationTeams.filter(t => !t.teamName?.toLowerCase().includes('junior'));
    const youthTeams = invitationTeams.filter(t => t.teamName?.toLowerCase().includes('junior'));
    // If we can't distinguish, show all in one list
    const showColumns = seniorTeams.length > 0 && youthTeams.length > 0;

    const handleTeamToggle = (teamId) => {
      setCoachData(prev => {
        const ids = prev.selectedTeamIds.includes(teamId)
          ? prev.selectedTeamIds.filter(id => id !== teamId)
          : [...prev.selectedTeamIds, teamId];
        return { ...prev, selectedTeamIds: ids };
      });
    };

    const renderTeamCheckbox = (team) => {
      const isSelected = coachData.selectedTeamIds.includes(team.teamId);
      const teamLabel = team.tier ? `${team.teamName} (${team.tier})` : team.teamName;
      const labelClass = `wizard-team-checkbox${isSelected ? ' wizard-team-checkbox--selected' : ''}`;

      return (
        <label key={team.teamId} className={labelClass}>
          <input
            type="checkbox"
            className="wizard-team-checkbox-input"
            checked={isSelected}
            onChange={() => handleTeamToggle(team.teamId)}
          />
          <span className="wizard-team-checkbox-label">{teamLabel}</span>
        </label>
      );
    };

    return (
      <div className="form-step">
        <h2>Wybierz drużyny</h2>
        <p style={{ marginBottom: '8px', color: '#666', textAlign: 'center', fontSize: '0.62rem' }}>
          Wybierz drużyny, w których pracujesz jako trener
        </p>

        {invitationTeams.length === 0 ? (
          <div className="error-message">Klub nie ma zdefiniowanych drużyn</div>
        ) : showColumns ? (
          <div className="wizard-teams-columns">
            <div className="wizard-teams-column">
              <h4 className="wizard-teams-heading wizard-teams-heading--senior">Seniorzy</h4>
              <div className="wizard-teams-list">
                {seniorTeams.map(renderTeamCheckbox)}
              </div>
            </div>
          </div>
        ) : (
          <div className="wizard-teams-list" style={{ maxWidth: '400px', margin: '0 auto' }}>
            {invitationTeams.map(renderTeamCheckbox)}
          </div>
        )}
      </div>
    );
  };

  const renderStep = () => {
    // TRENER (z zaproszenia)
    if (selectedUserType === 'COACH') {
      // ---- Istniejący użytkownik: tylko wybór drużyn ----
      if (invitationUserExists) {
        if (currentStep === 1) {
          return renderInvitationTeamSelection();
        }
        return null;
      }

      // ---- Nowy użytkownik: krok 1=dane osobowe, 2=drużyny, 3=hasło ----
      if (currentStep === 1) {
        return (
          <div className="form-step">
            <h2>Dane osobowe</h2>
            <div className="form-group">
              <label htmlFor="firstName">Imię<span className="required-asterisk">*</span></label>
              <input
                type="text"
                id="firstName"
                value={coachData.firstName}
                onChange={(e) => setCoachData(prev => ({ ...prev, firstName: e.target.value }))}
                placeholder="Twoje imię"
                maxLength={200}
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Nazwisko<span className="required-asterisk">*</span></label>
              <input
                type="text"
                id="lastName"
                value={coachData.lastName}
                onChange={(e) => setCoachData(prev => ({ ...prev, lastName: e.target.value }))}
                placeholder="Twoje nazwisko"
                maxLength={200}
              />
            </div>
          </div>
        );
      }

      if (currentStep === 2) {
        return renderInvitationTeamSelection();
      }

      if (currentStep === 3) {
        const emailFromInvitation = !!invitationToken && !!email;
        return (
          <div className="form-step">
            <h2>Utwórz konto</h2>
            <div className="form-group">
              <label htmlFor="email">Email<span className="required-asterisk">*</span></label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => !emailFromInvitation && handleEmailChange(e.target.value)}
                onBlur={handleEmailBlur}
                placeholder="Wprowadź adres email"
                className={emailError ? 'input-error' : ''}
                maxLength={100}
                readOnly={emailFromInvitation}
                disabled={emailFromInvitation}
                style={emailFromInvitation ? { backgroundColor: '#e8e8e8', color: '#999', cursor: 'not-allowed' } : undefined}
              />
              {emailFromInvitation && (
                <small style={{ display: 'block', marginTop: '4px', color: '#888' }}>
                  Email ustawiony na podstawie zaproszenia
                </small>
              )}
              {emailError && <span className="error-message">{emailError}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="password">Hasło<span className="required-asterisk">*</span></label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                onBlur={handlePasswordBlur}
                placeholder="Wprowadź hasło"
                className={passwordError ? 'input-error' : ''}
                maxLength={128}
              />
              <small style={{ display: 'block', marginTop: '4px', color: '#888' }}>
                Min. 8 znaków, 1 wielka litera, 1 mała litera, 1 cyfra
              </small>
              {passwordError && <span className="error-message">{passwordError}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Potwierdź hasło<span className="required-asterisk">*</span></label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={handleConfirmPasswordBlur}
                placeholder="Powtórz hasło"
                className={touched.confirmPassword && password && confirmPassword && password !== confirmPassword ? 'input-error' : ''}
                maxLength={128}
              />
              {touched.confirmPassword && password && confirmPassword && password !== confirmPassword && (
                <span className="error-message">Hasła nie są zgodne</span>
              )}
            </div>

            <div className="consent-group">
              <label className="consent-label">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="consent-checkbox"
                />
                <span className="consent-text">
                  Zapoznałem/am się z{' '}
                  <a href="/regulamin" target="_blank" rel="noopener noreferrer" className="consent-link">
                    Regulaminem
                  </a>
                  {' '}i{' '}
                  <a href="/polityka-prywatnosci" target="_blank" rel="noopener noreferrer" className="consent-link">
                    Polityką prywatności
                  </a>
                  {' '}i akceptuję ich postanowienia<span className="required-asterisk">*</span>
                </span>
              </label>
            </div>
          </div>
        );
      }
    }

    // KLUB: krok 1=dane klubu, 2=drużyny, 3=hasło
    if (selectedUserType === 'SPORTS_CLUB') {
      if (currentStep === 1) {
        return (
          <div className="form-step">
            <h2>Dane klubu</h2>
            <div className="form-group">
              <label htmlFor="regon">REGON<span className="required-asterisk">*</span></label>
              <input
                type="text"
                id="regon"
                value={clubData.regon}
                onChange={handleRegonChange}
                onBlur={handleRegonBlur}
                placeholder="REGON klubu"
                className={regonError ? 'input-error' : ''}
                maxLength={9}
              />
              {regonError && <span className="error-message">{regonError}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="nip">NIP<span className="required-asterisk">*</span></label>
              <input
                type="text"
                id="nip"
                value={clubData.nip}
                onChange={handleNipChange}
                onBlur={handleNipBlur}
                placeholder="NIP klubu"
                className={nipError ? 'input-error' : ''}
                maxLength={10}
              />
              {nipError && <span className="error-message">{nipError}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="clubName">Nazwa klubu<span className="required-asterisk">*</span></label>
              <input
                type="text"
                id="clubName"
                value={clubData.name}
                onChange={(e) => setClubData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Nazwa klubu"
                maxLength={200}
              />
            </div>
            <div className="form-group">
              <label htmlFor="voivodeship">Województwo<span className="required-asterisk">*</span></label>
              <select
                id="voivodeship"
                value={clubData.voivodeship}
                onChange={(e) => setClubData(prev => ({ ...prev, voivodeship: e.target.value, county: '' }))}
                className="form-select"
              >
                <option value="">-- Wybierz --</option>
                {territories.map((territory) => (
                  <option key={territory.key} value={territory.key}>
                    {territory.displayValue}
                  </option>
                ))}
              </select>
            </div>
            {clubData.voivodeship && (
              <div className="form-group">
                <label htmlFor="county">Powiat<span className="required-asterisk">*</span></label>
                <select
                  id="county"
                  value={clubData.county}
                  onChange={(e) => setClubData(prev => ({ ...prev, county: e.target.value }))}
                  className="form-select"
                >
                  <option value="">-- Wybierz --</option>
                  {getCountiesForVoivodeship(clubData.voivodeship).map((county) => (
                    <option key={county.key} value={county.key}>
                      {county.displayValue}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="form-group">
              <label>Adres boiska</label>
              <button
                type="button"
                onClick={() => setCityMapOpen(true)}
                style={{
                  width: '100%',
                  padding: '7px 10px',
                  borderRadius: '4px',
                  border: '1px solid #d0d0d0',
                  background: clubData.city ? '#e8f5e9' : '#fff',
                  cursor: 'pointer',
                  fontSize: '0.72rem',
                  textAlign: 'left',
                  color: clubData.city ? '#1b5e20' : '#999',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <span style={{ fontSize: '0.78rem' }}>📍</span>
                {clubData.city || 'Zaznacz lokalizację na mapie...'}
              </button>
            </div>
            <MapPickerModal
              isOpen={cityMapOpen}
              onConfirm={handleCityMapConfirm}
              onCancel={() => setCityMapOpen(false)}
              initialLat={clubData.latitude}
              initialLon={clubData.longitude}
              initialAddress={clubData.city}
            />
            <div className="form-group">
              <label htmlFor="clubLogo">Logo klubu</label>
              {logoPreview ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
                  <img
                    src={logoPreview}
                    alt="Podgląd logo"
                    style={{
                      maxWidth: '128px',
                      maxHeight: '128px',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                      objectFit: 'contain'
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="btn btn-danger btn-small"
                  >
                    Usuń logo
                  </button>
                </div>
              ) : (
                <input
                  type="file"
                  id="clubLogo"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleLogoChange}
                />
              )}
              {logoError && <span className="error-message">{logoError}</span>}
              <small style={{ display: 'block', marginTop: '4px', color: '#888', fontSize: '0.65rem' }}>
                Tylko format PNG. Maksymalna rozdzielczość: 1024x1024 px. Pole nieobowiązkowe.
              </small>
            </div>
          </div>
        );
      }

      if (currentStep === 2) {
        const seniorTeams = clubData.teams.filter(t => t.teamType === 'SENIOR');
        const youthTeams = clubData.teams.filter(t => t.teamType === 'YOUTH');

        return (
          <div className="form-step">
            <h2>Drużyny</h2>

            <div className="teams-sections-wrapper">
              {/* Sekcja drużyn seniorskich */}
              <div className="team-section">
                <div className="team-section-header">
                  <h3>Drużyny seniorskie</h3>
                  <button
                    type="button"
                    onClick={() => setShowSeniorTeamModal(true)}
                    className="btn btn-secondary"
                  >
                    Dodaj drużynę
                  </button>
                </div>

                {seniorTeams.length > 0 && (
                  <div className="teams-list">
                    {seniorTeams.map((team, globalIndex) => {
                      const index = clubData.teams.indexOf(team);
                      return (
                        <div key={index} className="team-item">
                          <div className="team-info">
                            <div className="team-info-row">
                              <strong>Rodzaj:</strong> {formatSeniorTeamType(team.seniorTeamType)}
                            </div>
                            <div className="team-info-row">
                              <strong>Poziom rozgrywkowy:</strong> {formatSeniorTier(team.seniorTier)}
                            </div>
                            {team.gender && (
                              <div className="team-info-row">
                                <strong>Płeć:</strong> {team.gender === 'MALE' ? 'Mężczyźni' : 'Kobiety'}
                              </div>
                            )}
                          </div>
                          <div className="team-actions">
                            <button
                              type="button"
                              onClick={() => editTeam(index)}
                              className="btn btn-secondary btn-small"
                            >
                              Edytuj
                            </button>
                            <button
                              type="button"
                              onClick={() => removeTeam(index)}
                              className="btn btn-danger btn-small"
                            >
                              Usuń
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

            </div>
          </div>
        );
      }

      if (currentStep === 3) {
        return (
          <div className="form-step">
            <h2>Utwórz konto</h2>
            <div className="form-group">
              <label htmlFor="email">Email<span className="required-asterisk">*</span></label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                onBlur={handleEmailBlur}
                placeholder="Wprowadź adres email"
                className={emailError ? 'input-error' : ''}
                maxLength={100}
              />
              {emailError && <span className="error-message">{emailError}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="password">Hasło<span className="required-asterisk">*</span></label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                onBlur={handlePasswordBlur}
                placeholder="Wprowadź hasło"
                className={passwordError ? 'input-error' : ''}
                maxLength={128}
              />
              <small style={{ display: 'block', marginTop: '4px', color: '#888' }}>
                Min. 8 znaków, 1 wielka litera, 1 mała litera, 1 cyfra
              </small>
              {passwordError && <span className="error-message">{passwordError}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Potwierdź hasło<span className="required-asterisk">*</span></label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={handleConfirmPasswordBlur}
                placeholder="Powtórz hasło"
                className={touched.confirmPassword && password && confirmPassword && password !== confirmPassword ? 'input-error' : ''}
                maxLength={128}
              />
              {touched.confirmPassword && password && confirmPassword && password !== confirmPassword && (
                <span className="error-message">Hasła nie są zgodne</span>
              )}
            </div>

            <div className="consent-group">
              <label className="consent-label">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="consent-checkbox"
                />
                <span className="consent-text">
                  Zapoznałem/am się z{' '}
                  <a href="/regulamin" target="_blank" rel="noopener noreferrer" className="consent-link">
                    Regulaminem
                  </a>
                  {' '}i{' '}
                  <a href="/polityka-prywatnosci" target="_blank" rel="noopener noreferrer" className="consent-link">
                    Polityką prywatności
                  </a>
                  {' '}i akceptuję ich postanowienia<span className="required-asterisk">*</span>
                </span>
              </label>
            </div>
          </div>
        );
      }
    }

    return null;
  };

  return (
    <div className="register-container">
      <div className={`register-card${invitationToken ? ' register-card--invitation' : ''}`}>
        <div className="register-header">
          <div className="register-icon">📝</div>
          <h1>{invitationUserExists ? 'Dołącz do drużyny' : 'Rejestracja'}</h1>
          <p>{invitationUserExists ? 'Wybierz drużyny z zaproszenia' : 'Utwórz nowe konto'}</p>
        </div>

        {safeReturnTo && !successMessage && (
          <div style={{ background: '#eef5ff', border: '1px solid #cfe1ff', color: '#1b3a6e', padding: '8px 12px', borderRadius: 6, fontSize: 13, margin: '0 auto 12px', maxWidth: 600 }}>
            Po rejestracji zostaniesz automatycznie zalogowany i przeniesiony do zaproszenia sparingowego.
          </div>
        )}

        {successMessage ? (
          <div className="success-container">
            <div className="success-message">
              <div className="success-icon">✓</div>
              <h2>{successMessage === 'COACH_ACCEPT_SUCCESS' ? 'Zgłoszenie wysłane!' : 'Rejestracja przebiegła pomyślnie!'}</h2>
              {successMessage === 'COACH_ACCEPT_SUCCESS' ? (
                <>
                  <p style={{ marginBottom: '8px' }}>
                    Twoje zgłoszenie do drużyny zostało wysłane. Klub musi jeszcze zatwierdzić Twoje zgłoszenie.
                  </p>
                  <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '16px' }}>
                    Gdy klub zaakceptuje Twoją prośbę, uzyskasz dostęp do wybranych drużyn.
                  </p>
                </>
              ) : successMessage === 'COACH_SUCCESS' ? (
                <>
                  <p style={{ marginBottom: '8px' }}>
                    Twoje konto zostało utworzone i zostałeś przypisany do wybranych drużyn.
                  </p>
                  <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '16px' }}>
                    Możesz teraz zalogować się i korzystać z platformy.
                  </p>
                </>
              ) : (
                <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '16px' }}>
                    Na adres email podany podczas rejestracji zostanie wysłana wiadomość z instrukcjami niezbędnymi do aktywacji profilu klubu. Możesz się zalogować aby dodać terminy i zweryfikować drużyny w Twojej okolicy.
                  </p>
              )}
              <Link
                to={safeReturnTo ? `/login?returnTo=${encodeURIComponent(safeReturnTo)}` : '/login'}
                className="btn btn-primary"
              >
                Przejdź do logowania
              </Link>
            </div>
          </div>
        ) : invitationLoading ? (
          <div className="loading-message" style={{ padding: '40px', textAlign: 'center', color: '#7f8c8d' }}>
            Weryfikacja zaproszenia...
          </div>
        ) : invitationError ? (
          <div className="error-message" style={{ margin: '20px 0' }}>
            {invitationError}
            <div style={{ marginTop: '16px' }}>
              <Link to="/register" className="btn btn-primary">
                Zarejestruj się jako klub
              </Link>
            </div>
          </div>
        ) : (
          <>
            {invitationClubName && (
              <div className="invitation-club-banner">
                Zaproszenie od klubu: <strong>{invitationClubName}</strong>
              </div>
            )}

            {getTotalSteps() > 1 && renderStepIndicator()}

            <form onSubmit={handleSubmit} className="register-form">
              {error && <div className="error-message">{error}</div>}

              {/* Honeypot — ukryte pole anty-bot */}
              <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                <label htmlFor="company_url">Company URL</label>
                <input
                  type="text"
                  id="company_url"
                  name="company_url"
                  tabIndex={-1}
                  autoComplete="off"
                  onChange={(e) => { honeypotRef.current = e.target.value; }}
                />
              </div>

              {renderStep()}

              <div className="form-navigation">
                {currentStep > 1 && (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="btn btn-secondary"
                  >
                    Wstecz
                  </button>
                )}

                {currentStep < getTotalSteps() ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="btn btn-primary"
                    disabled={!isCurrentStepValid()}
                  >
                    Dalej
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading || !isCurrentStepValid() || (registerLockoutUntil && Date.now() < registerLockoutUntil)}
                  >
                    {isLoading
                      ? (invitationUserExists ? 'Wysyłanie...' : 'Rejestrowanie...')
                      : (invitationUserExists ? 'Dołącz do drużyny' : 'Zarejestruj się')
                    }
                  </button>
                )}
              </div>
            </form>
          </>
        )}

        {!successMessage && !invitationUserExists && (
          <div className="register-footer">
            <p>Masz już konto?</p>
            <Link to="/login" state={{ from }} className="login-link">
              Zaloguj się
            </Link>
          </div>
        )}
      </div>

      {/* Modal dodawania drużyny seniorskiej */}
      {showSeniorTeamModal && (
        <div className="modal-overlay" onClick={() => {
          setShowSeniorTeamModal(false);
          setEditingTeamIndex(null);
          setNewSeniorTeam({ teamType: 'SENIOR', seniorTier: '', seniorTeamType: '', gender: '', teamMode: 'SEASONAL', isLookingForPlayer: false, isLookingForCoach: false });
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingTeamIndex !== null ? 'Edytuj drużynę seniorską' : 'Dodaj drużynę seniorską'}</h2>
            <div className="form-group">
              <label htmlFor="seniorTeamTier">Poziom rozgrywkowy</label>
              <select
                id="seniorTeamTier"
                value={newSeniorTeam.seniorTier}
                onChange={(e) => setNewSeniorTeam(prev => ({ ...prev, seniorTier: e.target.value }))}
                className="form-select"
              >
                <option value="">-- Wybierz --</option>
                <option value="EKSTRAKLASA">Ekstraklasa</option>
                <option value="FIRST_LEAGUE">I Liga</option>
                <option value="SECOND_LEAGUE">II Liga</option>
                <option value="THIRD_LEAGUE">III Liga</option>
                <option value="FOURTH_LEAGUE">IV Liga</option>
                <option value="FIFTH_LEAGUE">V Liga</option>
                <option value="REGIONAL_CLASS">Klasa Okręgowa</option>
                <option value="CLASS_A">Klasa A</option>
                <option value="CLASS_B">Klasa B</option>
                <option value="CLASS_C">Klasa C</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="seniorTeamType">Rodzaj drużyny</label>
              <select
                id="seniorTeamType"
                value={newSeniorTeam.seniorTeamType}
                onChange={(e) => setNewSeniorTeam(prev => ({ ...prev, seniorTeamType: e.target.value }))}
                className="form-select"
              >
                <option value="">-- Wybierz --</option>
                <option value="FIRST_TEAM">Pierwsza drużyna</option>
                <option value="SECOND_TEAM">Druga drużyna</option>
                <option value="THIRD_TEAM">Trzecia drużyna</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="seniorTeamGender">Płeć</label>
              <select
                id="seniorTeamGender"
                value={newSeniorTeam.gender}
                onChange={(e) => setNewSeniorTeam(prev => ({ ...prev, gender: e.target.value }))}
                className="form-select"
              >
                <option value="">-- Wybierz --</option>
                <option value="MALE">Mężczyźni</option>
                <option value="FEMALE">Kobiety</option>
              </select>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                onClick={() => {
                  setShowSeniorTeamModal(false);
                  setEditingTeamIndex(null);
                  setNewSeniorTeam({ teamType: 'SENIOR', seniorTier: '', seniorTeamType: '', gender: '', teamMode: 'SEASONAL', isLookingForPlayer: false, isLookingForCoach: false });
                }}
                className="btn btn-secondary"
              >
                Anuluj
              </button>
              <button
                type="button"
                onClick={addSeniorTeam}
                className="btn btn-primary"
                disabled={!newSeniorTeam.seniorTeamType || !newSeniorTeam.seniorTier || !newSeniorTeam.gender}
              >
                {editingTeamIndex !== null ? 'Zapisz' : 'Dodaj'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal wizarda dodawania drużyny trenera */}
      {showCoachAddTeamModal && (
        <div className="modal-overlay" onClick={closeCoachAddTeamModal}>
          <div className="modal-content coach-add-team-modal" onClick={(e) => e.stopPropagation()}>
            {/* Wizard step indicator */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', justifyContent: 'center' }}>
              <div style={{
                width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: coachWizardStep === 1 ? '#4caf50' : '#2e7d32', color: 'white', fontWeight: 'bold', fontSize: '14px',
                boxShadow: coachWizardStep === 1 ? '0 4px 12px rgba(76, 175, 80, 0.3)' : 'none'
              }}>
                {coachWizardStep > 1 ? '✓' : '1'}
              </div>
              <div style={{ width: '40px', height: '2px', alignSelf: 'center', backgroundColor: coachWizardStep > 1 ? '#4caf50' : '#ddd' }} />
              <div style={{
                width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: coachWizardStep === 2 ? '#4caf50' : '#e0e0e0', color: coachWizardStep === 2 ? 'white' : '#999', fontWeight: 'bold', fontSize: '14px',
                boxShadow: coachWizardStep === 2 ? '0 4px 12px rgba(76, 175, 80, 0.3)' : 'none'
              }}>
                2
              </div>
            </div>

            {coachWizardStep === 1 && (
              <>
                <h2 style={{ textAlign: 'center' }}>Wyszukaj klub</h2>
                <p style={{ marginBottom: '16px', color: '#666', textAlign: 'center' }}>
                  Użyj filtrów, aby znaleźć klub, w którym pracujesz jako trener
                </p>

                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <div className="form-group" style={{ flex: '1', minWidth: '200px' }}>
                    <label htmlFor="wizard-voivodeship">Województwo</label>
                    <select
                      id="wizard-voivodeship"
                      name="voivodeship"
                      value={coachSearchCriteria.voivodeship}
                      onChange={handleCoachSearchChange}
                      className="form-select"
                    >
                      <option value="">-- Wszystkie --</option>
                      {territories.map((territory) => (
                        <option key={territory.key} value={territory.key}>
                          {territory.displayValue}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: '1', minWidth: '200px' }}>
                    <label htmlFor="wizard-county">Powiat</label>
                    <select
                      id="wizard-county"
                      name="county"
                      value={coachSearchCriteria.county}
                      onChange={handleCoachSearchChange}
                      className="form-select"
                      disabled={!coachSearchCriteria.voivodeship}
                    >
                      <option value="">-- Wszystkie --</option>
                      {getCountiesForCoachSearch().map((county) => (
                        <option key={county.key} value={county.key}>
                          {county.displayValue}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', marginTop: '16px', marginBottom: '16px', justifyContent: 'center' }}>
                  <button type="button" onClick={handleResetCoachSearch} className="btn btn-secondary">
                    Wyczyść
                  </button>
                  <button type="button" onClick={handleCoachSearch} className="btn btn-primary" disabled={isLoadingCoachSearch}>
                    {isLoadingCoachSearch ? 'Wyszukiwanie...' : 'Szukaj'}
                  </button>
                </div>

                {coachSearchError && <div className="error-message">{coachSearchError}</div>}

                {coachSearchResults.length > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <h4>Wyniki ({coachSearchResults.length} z {coachSearchPagination.totalElements})</h4>
                    <div className="results-grid">
                      {coachSearchResults.map((club, index) => (
                        <div
                          key={index}
                          className={`result-card ${coachData.selectedClubId === club.id ? 'selected' : ''}`}
                          style={{ cursor: 'pointer' }}
                        >
                          {club.logoUrl && (
                            <img src={club.logoUrl} alt={`Logo ${club.name}`}
                              style={{ width: '64px', height: '64px', objectFit: 'contain', alignSelf: 'center', borderRadius: '4px' }}
                            />
                          )}
                          <h5>{club.name}</h5>
                          <div className="team-info">
                            <p><strong>Woj.:</strong> {club.voivodeship}</p>
                            <p><strong>Pow.:</strong> {club.county}</p>
                          </div>
                          <button type="button" className="btn btn-primary btn-select" onClick={() => handleSelectClub(club)}>
                            {coachData.selectedClubId === club.id ? 'Wybrano' : 'Wybierz'}
                          </button>
                        </div>
                      ))}
                    </div>

                    {coachSearchPagination.totalPages > 1 && (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', marginTop: '16px' }}>
                        <button type="button" onClick={handleCoachSearchPreviousPage} disabled={coachSearchPagination.isFirst || isLoadingCoachSearch} className="btn btn-secondary">
                          Poprzednia
                        </button>
                        <span style={{ fontSize: '0.9rem', color: '#666' }}>
                          {coachSearchPagination.currentPage + 1} / {coachSearchPagination.totalPages}
                        </span>
                        <button type="button" onClick={handleCoachSearchNextPage} disabled={coachSearchPagination.isLast || isLoadingCoachSearch} className="btn btn-secondary">
                          Następna
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="modal-actions" style={{ marginTop: '20px' }}>
                  <button type="button" onClick={closeCoachAddTeamModal} className="btn btn-secondary">Anuluj</button>
                  <button
                    type="button"
                    onClick={() => { setCoachWizardStep(2); setWizardTempSelectedTeamIds([]); }}
                    className="btn btn-primary"
                    disabled={!coachData.selectedClubId}
                  >
                    Dalej
                  </button>
                </div>
              </>
            )}

            {coachWizardStep === 2 && (() => {
              const seniorTeams = coachTeams.filter(team => team.teamType === 'SENIOR' || team.teamType === 'Seniorzy');
              const youthTeams = coachTeams.filter(team => team.teamType === 'YOUTH' || team.teamType === 'Juniorzy');
              const alreadyAdded = coachData.selectedTeamIds;

              const handleWizardTeamToggle = (teamId) => {
                setWizardTempSelectedTeamIds(prev =>
                  prev.includes(teamId) ? prev.filter(id => id !== teamId) : [...prev, teamId]
                );
              };

              const renderWizardTeamCheckbox = (team) => {
                const isAlreadyAdded = alreadyAdded.includes(team.teamId);
                const isSelected = wizardTempSelectedTeamIds.includes(team.teamId);
                const teamName = (team.teamType === 'SENIOR' || team.teamType === 'Seniorzy')
                  ? (team.name || 'Drużyna seniorska')
                  : (team.name || 'Drużyna juniorska');
                const teamLabel = team.tier ? `${teamName} (${team.tier})` : teamName;

                const labelClass = `wizard-team-checkbox${isAlreadyAdded ? ' wizard-team-checkbox--disabled' : ''}${isSelected ? ' wizard-team-checkbox--selected' : ''}`;

                return (
                  <label key={team.teamId} className={labelClass}>
                    <input
                      type="checkbox"
                      className="wizard-team-checkbox-input"
                      checked={isSelected || isAlreadyAdded}
                      onChange={() => !isAlreadyAdded && handleWizardTeamToggle(team.teamId)}
                      disabled={isAlreadyAdded}
                    />
                    <span className="wizard-team-checkbox-label">
                      {teamLabel}
                      {isAlreadyAdded && <span className="wizard-team-checkbox-added">(dodano)</span>}
                    </span>
                  </label>
                );
              };

              return (
                <>
                  <h2>Wybierz drużyny</h2>
                  <p style={{ marginBottom: '12px', color: '#666' }}>
                    Wybierz drużyny z tego klubu, w których pracujesz jako trener
                  </p>

                  {isLoadingTeams ? (
                    <p>Ładowanie drużyn...</p>
                  ) : coachTeams.length === 0 ? (
                    <div className="error-message">Wybrany klub nie ma zdefiniowanych drużyn</div>
                  ) : (
                    <div className="wizard-teams-columns">
                      <div className="wizard-teams-column">
                        <h4 className="wizard-teams-heading wizard-teams-heading--senior">Seniorzy</h4>
                        <div className="wizard-teams-list">
                          {seniorTeams.length === 0
                            ? <p className="wizard-teams-empty">Brak drużyn seniorskich</p>
                            : seniorTeams.map(renderWizardTeamCheckbox)}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="modal-actions" style={{ marginTop: '20px' }}>
                    <button type="button" onClick={() => setCoachWizardStep(1)} className="btn btn-secondary">Wstecz</button>
                    <button
                      type="button"
                      onClick={handleWizardConfirmTeams}
                      className="btn btn-primary"
                      disabled={wizardTempSelectedTeamIds.length === 0}
                    >
                      Dodaj wybrane ({wizardTempSelectedTeamIds.length})
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

    </div>
  );
}

export default Register;
